import * as React from "react";
import { cn } from "../../../lib/utils";
import { Link } from "../Link";

export interface ErrorPageProps {
  /**
   * The error code to display (e.g., "404", "403", "500")
   */
  code?: string;
  /**
   * The main title/heading for the error
   */
  title: string;
  /**
   * A description explaining what went wrong
   */
  description: string;
  /**
   * The illustration/image to display
   */
  illustration?: React.ReactNode;
  /**
   * The URL to navigate to when clicking "Go Home" (defaults to "/")
   */
  homeUrl?: string;
  /**
   * Custom label for the home button (defaults to "Go Home")
   */
  homeLabel?: string;
  /**
   * Additional actions to display below the main button
   */
  additionalActions?: React.ReactNode;
  /**
   * Additional class names for the container
   */
  className?: string;
}

function ErrorPage({
  code,
  title,
  description,
  illustration,
  homeUrl = "/",
  homeLabel = "Go Home",
  additionalActions,
  className,
}: ErrorPageProps) {
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center min-h-[60vh] p-4 text-center",
        className,
      )}
    >
      {illustration && (
        <div className="mb-2 max-w-md w-full">{illustration}</div>
      )}

      {code && <p className="text-6xl font-bold text-primary mb-2">{code}</p>}

      <h1 className="text-2xl font-semibold text-foreground mb-2">{title}</h1>

      <p className="text-muted-foreground max-w-md mb-8">{description}</p>

      <div className="flex flex-col sm:flex-row gap-3">
        <Link href={homeUrl} variant="secondary">
          {homeLabel}
        </Link>
        {additionalActions}
      </div>
    </div>
  );
}

ErrorPage.displayName = "ErrorPage";

export { ErrorPage };
